<template>
	<pop-up-wrapper @close="$emit('close')">
		<div class="pop-up-share-resource">
			<img
				@click="$emit('close')"
				class="close"
				src="/img/close.svg"
				alt=""
			/>
			<p class="title">Partager aux professeurs</p>

			<form @submit.prevent="submitShare()" class="all-filter-container">
				<div class="all-filter-wrapper">
					<div class="filter-wrapper">
						<label for="subject">Matière *</label>
						<select
							name="subject"
							id="subject"
							v-model="selectedSubject"
							@change="onSubjectChange"
						>
							<option value="">Choisir une matière</option>
							<option
								v-for="subject in getUserSubjects()"
								:key="subject.id"
								:value="subject.id"
							>
								{{ subject.name }}
							</option>
						</select>
					</div>

					<div class="filter-wrapper">
						<label for="level">Niveau</label>
						<select
							disabled="disabled"
							name="level"
							id="level"
							v-model="selectedLevel"
							@change="onLevelChange"
						>
							<option value="">Choisir un niveau</option>
							<option
								v-for="level in getUserLevels()"
								:key="level.id"
								:value="level.id"
							>
								{{ level.name }}
							</option>
						</select>
					</div>

					<div v-if="showChapter" class="filter-wrapper">
						<label for="chapter">Chapitre</label>
						<select
							v-model="selectedChapter"
							name="chapter"
							id="chapter"
						>
							<option value="">Choisir un chapitre</option>
							<optgroup
								v-for="sequence in getSequence()"
								:key="sequence.id"
								:label="sequence.name"
							>
								<option
									v-for="chapter in $store.getters.chapters(
										sequence.id
									)"
									:key="chapter.id"
									:value="chapter.id"
								>
									{{ chapter.name }}
								</option>
							</optgroup>
						</select>
					</div>

					<div class="filter-wrapper">
						<label>Type d'accès</label>
						<div class="access-type-buttons">
							<label class="access-option">
								<input
									type="radio"
									v-model="accessType"
									value="public"
									name="accessType"
								/>
								<span class="radio-custom"></span>
								Ouvert à tous
							</label>
							<label class="access-option">
								<input
									type="radio"
									v-model="accessType"
									value="limited"
									name="accessType"
								/>
								<span class="radio-custom"></span>
								Limiter l'accès
							</label>
						</div>
					</div>

					<div v-if="accessType === 'limited'" class="filter-wrapper">
						<label
							>Partager avec des professeurs spécifiques *</label
						>
						<div class="email-input-container">
							<input
								type="email"
								v-model="newEmail"
								placeholder="Email du professeur"
								@keyup.enter="addTeacherEmail"
							/>
							<button
								type="button"
								@click="addTeacherEmail"
								class="button button--secondary button--small"
							>
								Ajouter
							</button>
						</div>
						<div
							v-if="teacherEmails.length > 0"
							class="emails-list"
						>
							<div
								v-for="email in teacherEmails"
								:key="email"
								class="email-item"
							>
								<span>{{ email }}</span>
								<button
									type="button"
									@click="removeTeacherEmail(email)"
									class="remove-email"
								>
									×
								</button>
							</div>
						</div>
					</div>
				</div>
				<button
					type="submit"
					class="button button--primary button--full"
				>
					{{ isModified ? "Sauvegarder" : "Partager" }}
				</button>
				<button
					type="button"
					@click="stopShare()"
					class="button button--secondary button--full"
				>
					Arrêter le partage
				</button>
			</form>
		</div>
	</pop-up-wrapper>
</template>

<script>
import PopUpWrapper from "./PopUpWrapper.vue";
import { authenticatedApiFetch } from "../../utils/utils";

export default {
	components: { PopUpWrapper },
	props: {
		id: Number,
		type: {
			type: String,
			default: "file",
			validator: (value) => ["file", "folder"].indexOf(value) !== -1,
		},
		classId: Number,
		sharedWithSpecificTeachers: {
			type: Array,
			default: () => [],
		},
		// Props pour récupérer les informations du fichier/dossier
		fileData: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			selectedSubject: "",
			selectedLevel: "",
			selectedChapter: "",
			showChapter: true,
			accessType: "public",
			newEmail: "",
			teacherEmails: [],
			// Ajout des valeurs initiales pour comparaison
			initialSubject: "",
			initialLevel: "",
			initialChapter: "",
			initialTeacherEmails: [],
		};
	},
	watch: {
		accessType(newValue, oldValue) {
			// Si on passe d'un accès limité à un accès ouvert, vider le tableau des emails
			if (oldValue === 'limited' && newValue === 'public') {
				this.teacherEmails = [];
			}
		}
	},
	computed: {
		isModified() {
			return (
				this.selectedSubject !== this.initialSubject ||
				this.selectedLevel !== this.initialLevel ||
				this.selectedChapter !== this.initialChapter ||
				this.teacherEmails.join(",") !==
					this.initialTeacherEmails.join(",")
			);
		},
	},
	created() {
		// Auto-sélection du niveau basé sur la classe courante
		if (this.classId) {
			const classes = this.$store.state.classes || [];
			const classe = classes.find(
				(c) => c.id === this.classId || c.id === parseInt(this.classId)
			);

			if (classe && (classe.level_id || classe.level)) {
				this.selectedLevel = classe.level_id || classe.level;
			}
		}

		// Remplir automatiquement la matière et le chapitre si le fichier est déjà partagé
		if (this.fileData && this.fileData.subject) {
			this.selectedSubject = this.fileData.subject;
			this.initialSubject = this.fileData.subject;
		}
		if (this.fileData && this.fileData.chapter) {
			this.selectedChapter = this.fileData.chapter;
			this.initialChapter = this.fileData.chapter;
		} else if (this.fileData && this.fileData.chapter === null) {
			this.selectedChapter = "";
			this.initialChapter = "";
		}
		if (this.fileData && this.fileData.level) {
			// Extraire l'ID du niveau (gère les cas où c'est un objet ou un ID direct)
			const levelId = this.fileData.level.id || this.fileData.level;
			this.selectedLevel = levelId;
			this.initialLevel = levelId;
		}
		
		if (
			this.sharedWithSpecificTeachers &&
			this.sharedWithSpecificTeachers.length > 0
		) {
			this.teacherEmails = [...this.sharedWithSpecificTeachers];
			this.initialTeacherEmails = [...this.sharedWithSpecificTeachers];
			this.accessType = "limited";
		} else {
			this.initialTeacherEmails = [];
		}
	},
	methods: {
		async addTeacherEmail() {
			const raw = this.newEmail || "";
			const email = raw.trim().toLowerCase();
			if (!email) return;

			// Validation rapide du format email
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email)) {
				this.$store.state.toast = {
					status: "error",
					text: "Email invalide",
				};
				return;
			}

			if (this.teacherEmails.includes(email)) {
				this.$store.state.toast = {
					status: "error",
					text: "Email déjà ajouté",
				};
				return;
			}

			try {
				const res = await authenticatedApiFetch(
					`/check-teacher-email/?email=${encodeURIComponent(email)}`,
					{ method: "GET" }
				);
				const json = await res.json();
				if (json && (json.exists === true || json.valid === true)) {
					this.teacherEmails.push(email);
					this.newEmail = "";
					this.accessType = "limited";
				} else {
					this.$store.state.toast = {
						status: "error",
						text: "Cet email n'existe pas",
					};
				}
			} catch (e) {
				// En cas d'erreur réseau/API, ne pas ajouter l'email
				this.$store.state.toast = {
					status: "error",
					text: "Vérification impossible. Réessayez.",
				};
			}
		},
		removeTeacherEmail(email) {
			this.teacherEmails = this.teacherEmails.filter((e) => e !== email);
			if (this.teacherEmails.length === 0) {
				this.accessType = "public";
			}
		},
		getUserSubjects() {
			const subjects = this.$store.getters.subjects(
				this.$store.state.user.subjects
			);
			return subjects;
		},
		getUserLevels() {
			const levels = this.$store.getters.levels(
				this.$store.state.user.levels
			);
			return levels;
		},
		getSequence() {
			if (this.$store.state.user.subjects.length == 1) {
				this.selectedSubject = this.$store.state.user.subjects[0];
			}
			return this.$store.getters.sequencesByLevelAndSubject(
				this.selectedLevel,
				this.selectedSubject
			);
		},
		onSubjectChange() {
			// Reset chapter when subject changes
			this.selectedChapter = "";
		},
		onLevelChange() {
			// Reset chapter when level changes
			this.selectedChapter = "";
		},
		stopShare() {
			// Émettre l'événement pour arrêter le partage
			this.$emit("stopShare");
			this.$emit("close");
		},
		submitShare() {
			if (!this.selectedSubject) {
				this.$store.state.toast = {
					text: "Veuillez choisir une matière",
					status: "error",
				};
				return;
			}

			// Ajout automatique de l'email de l'utilisateur si accès limité et aucun email spécifique
			if (
				this.accessType === "limited" &&
				this.teacherEmails.length === 0 &&
				this.$store.state.user?.username
			) {
				this.teacherEmails.push(this.$store.state.user.username);
			}

			const shareData = {
				subject: this.selectedSubject,
				level: this.selectedLevel,
				chapter: this.selectedChapter,
				is_public: this.accessType === "public",
				shared_with_specific_teachers: this.teacherEmails,
			};

			console.log(shareData);
			this.$emit("submit", shareData);
			this.$emit("close");
		},
	},
	emits: ["close", "submit", "stopShare"],
};
</script>

<style scoped>
.email-input-container {
	display: flex;
	gap: 10px;
	margin-top: 5px;
}

.email-input-container input {
	flex: 1;
	padding: 8px;
	border: 1px solid #ddd;
	border-radius: 4px;
}

.emails-list {
	margin-top: 10px;
}

.email-item {
	display: flex;
	justify-content: space-between;
	gap: 10px;
	align-items: center;
	padding: 5px 10px;
	background: #f5f5f5;
	border-radius: 4px;
	margin-bottom: 5px;
}

.remove-email {
	background: #ff4444;
	color: white;
	border: none;
	border-radius: 50%;
	width: 20px;
	height: 20px;
	cursor: pointer;
	font-size: 14px;
	line-height: 1;
}

.remove-email:hover {
	background: #cc0000;
}

.access-type-buttons {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.access-option {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
}
</style>
